import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, from, map, Observable, of, switchMap, throwError } from 'rxjs';

/**
 * For `responseType: 'blob'` requests (e.g. exports) the backend's JSON error body
 * arrives as a Blob. Parse it so the message can be shown like any other error.
 */
function normalizeError(error: HttpErrorResponse): Observable<HttpErrorResponse> {
  if (!(error.error instanceof Blob)) return of(error);
  return from(error.error.text()).pipe(
    map(text => {
      let body: unknown = text;
      try { body = JSON.parse(text); } catch { /* not JSON, keep raw text */ }
      return new HttpErrorResponse({
        error: body,
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined,
      });
    }),
    catchError(() => of(error)),
  );
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const isLogin = req.url.includes('/auth/login');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isLogin) {
        return throwError(() => error);
      }

      if (req.url.includes('/auth/refresh')) {
        return throwError(() => error);
      }

      return normalizeError(error).pipe(
        switchMap(err => {
          messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error?.message ?? '',
            life: 4000
          });
          return throwError(() => err);
        })
      );
    })
  );
};
